import * as THREE from 'three';
import { EXTRUDED_PROFILE_SURFACE_BIAS } from '../../_data/frameExtent.js';
import { resolveAnchorPlacedLabelOffset } from './labelAnchorPlacement.js';
import { sectorSliceHalfHeight } from './sectorSliceSurface.js';
const DEFAULT_COLOR = '#ffffff';
const DEFAULT_FONT_SIZE = 0.24;
const DEFAULT_PADDING = 0.12;
const DEFAULT_BACKGROUND_OPACITY = 0.65;
const DEFAULT_ABOVE_ITEM_CLEARANCE = 0.3;
const DEFAULT_ABOVE_LABEL_GAP = 0.05;
const DEFAULT_ON_ITEM_OFFSET = [0, 0.01, 0];
const DEFAULT_ORIENTATION = {
    orientationKind: 'fixed',
    plane: 'upright',
    facing: 'outward',
};
function normalizeTuple(tuple) {
    if (!tuple)
        return [0, 0, 0];
    return [tuple[0] ?? 0, tuple[1] ?? 0, tuple[2] ?? 0];
}
function addTuples(base, extra) {
    return [base[0] + extra[0], base[1] + extra[1], base[2] + extra[2]];
}
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(1, value));
}
/** Half-extent along the item's local up axis, so above-labels clear any shape rather than only spheres. */
function toShapeHalfHeight(shape) {
    if (shape === undefined)
        return 0;
    if (shape.shapeKind === 'sphere')
        return shape.radius;
    if (shape.shapeKind === 'panel')
        return shape.height / 2;
    if (shape.shapeKind === 'cylinder-wall')
        return shape.height / 2;
    if (shape.shapeKind === 'sector-slice')
        return sectorSliceHalfHeight(shape.innerRadius, shape.outerRadius, shape.outerThickness);
    if (shape.shapeKind === 'extruded-profile') {
        return Math.max(shape.from[1], shape.to[1])
            + Math.hypot(shape.innerWidth / 2 + shape.lipThickness, shape.lipHeight + shape.floorDepth + EXTRUDED_PROFILE_SURFACE_BIAS);
    }
    return 0;
}
function toChannelDefaultOffset(channel, shape, fontSize, padding) {
    if (channel === 'onItem')
        return DEFAULT_ON_ITEM_OFFSET;
    if (channel === 'backItem') {
        if (shape?.shapeKind === 'sector-slice') {
            return [0, 0, shape.outerRadius + 0.01];
        }
        return [0, 0, 0];
    }
    const labelHalfHeightWithPadding = fontSize / 2 + padding;
    const clearance = Math.max(DEFAULT_ABOVE_ITEM_CLEARANCE, labelHalfHeightWithPadding + DEFAULT_ABOVE_LABEL_GAP);
    return [0, toShapeHalfHeight(shape) + clearance, 0];
}
/**
 * Panel surfaces span local z in [-sag, 0] (curved panels bend toward -z). Each text face must sit
 * beyond that span on the side it is read from, or the opaque panel occludes it.
 */
export function resolvePanelLabelFaceDepths(args) {
    const { shape, position, rotation, fontSize } = args;
    const alignment = new THREE.Vector3(0, 0, 1).applyQuaternion(rotation).z;
    if (Math.abs(alignment) < 0.99)
        return undefined;
    const sign = Math.sign(alignment);
    const radius = shape.curvatureRadius;
    const sag = radius === undefined ? 0 : radius * (1 - Math.cos(Math.min(Math.PI / 2, shape.width / radius / 2)));
    const clearance = Math.max(0.01, fontSize * 0.02);
    const labelDepth = sign * position[2];
    const surfaceTop = sign > 0 ? 0 : sag;
    const surfaceBottom = sign > 0 ? -sag : 0;
    const lift = Math.max(labelDepth - surfaceTop, clearance);
    return { front: surfaceTop + lift - labelDepth, back: surfaceBottom - lift - labelDepth };
}
function toEuler(quaternion) {
    const euler = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ');
    return [euler.x === 0 ? 0 : euler.x, euler.y === 0 ? 0 : euler.y, euler.z === 0 ? 0 : euler.z];
}
function toLocalRotation(orientation) {
    if (orientation.orientationKind === 'billboard') {
        return new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, orientation.roll ?? 0, 'XYZ'));
    }
    const plane = orientation.plane === 'flat' ? [-Math.PI / 2, 0, 0] : [0, 0, 0];
    const facing = orientation.facing !== 'inward'
        ? [0, 0, 0]
        : orientation.plane === 'flat' ? [0, 0, Math.PI] : [0, Math.PI, 0];
    const user = normalizeTuple(orientation.rotation);
    const toQuaternion = (angles) => new THREE.Quaternion()
        .setFromEuler(new THREE.Euler(angles[0], angles[1], angles[2], 'XYZ'));
    return toQuaternion(plane).multiply(toQuaternion(facing)).multiply(toQuaternion(user));
}
/** The offset a placement declares, before any channel default is added. */
export function toDeclaredPlacementOffset(placement) {
    if (placement === undefined)
        return [0, 0, 0];
    return normalizeTuple(placement.placementKind === 'anchor' ? placement.localOffset : placement.offset);
}
/**
 * The single place label defaults and overrides are decided. Precedence is the order of the steps
 * below, rather than the order in which renderer stages happen to run.
 */
export function resolveLabel(args) {
    const { item, channel, declaration, shape, itemOpacity, labelFrameRotation, labelParentRotation, transformResolver, anchorContext, labelAnchorWorldPosition, layoutOverride, hoverFontScale, } = args;
    const text = declaration.text;
    if (text.trim().length === 0)
        return null;
    const fontSize = (layoutOverride?.fontSize ?? declaration.fontSize ?? DEFAULT_FONT_SIZE)
        * (hoverFontScale ?? 1);
    const padding = declaration.padding ?? DEFAULT_PADDING;
    const placement = declaration.placement ?? { placementKind: 'channel-default' };
    const defaultPosition = (() => {
        if (placement.placementKind === 'anchor') {
            if (anchorContext === undefined || !labelAnchorWorldPosition)
                return [0, 0, 0];
            return resolveAnchorPlacedLabelOffset({
                anchorRef: placement.anchorRef,
                localOffset: normalizeTuple(placement.localOffset),
                context: anchorContext,
                itemWorldPosition: labelAnchorWorldPosition,
                labelFrameRotation,
            }) ?? [0, 0, 0];
        }
        const base = placement.placementKind === 'item-origin'
            ? [0, 0, 0]
            : channel === 'aboveItem' && declaration.flag
                ? [0, toShapeHalfHeight(shape) + Math.abs(declaration.flag.postOffset[1]), 0]
                : toChannelDefaultOffset(channel, shape, fontSize, padding);
        return addTuples(base, layoutOverride?.offset ?? normalizeTuple(placement.offset));
    })();
    const orientation = declaration.orientation ?? DEFAULT_ORIENTATION;
    const billboard = orientation.orientationKind === 'billboard';
    const defaultLocalRotation = toLocalRotation(orientation);
    if (channel === 'onItem' && shape?.shapeKind === 'sector-slice' && (shape.outerThickness ?? 0) > 0
        && shape.thicknessProfile !== 'uniform' && !billboard) {
        const slope = sectorSliceHalfHeight(shape.innerRadius, shape.outerRadius, shape.outerThickness)
            / (shape.outerRadius - shape.innerRadius);
        defaultLocalRotation.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.atan(slope)));
    }
    const transform = transformResolver?.({
        item,
        channel,
        declaration,
        defaultPosition,
        defaultLocalRotation,
        ...(labelFrameRotation ? { labelFrameRotation } : {}),
    });
    let rotation = (() => {
        const custom = transform?.localRotation;
        if (!custom)
            return defaultLocalRotation.clone();
        return transform.localRotationMode === 'replace'
            ? custom.clone()
            : defaultLocalRotation.clone().multiply(custom);
    })();
    if (transform?.additionalFrameRotation) {
        rotation = transform.additionalFrameRotation.clone().multiply(rotation);
    }
    // A billboard faces the camera, so inheriting the constellation frame would only tip it out of view.
    if (labelFrameRotation && !billboard) {
        rotation = labelFrameRotation.clone().multiply(rotation);
    }
    if (labelParentRotation && !billboard) {
        rotation = labelParentRotation.clone().invert().multiply(rotation);
    }
    const position = transform?.position ?? defaultPosition;
    const doubleSidedReadable = declaration.doubleSidedReadable
        ?? (channel === 'onItem' && shape?.shapeKind === 'panel');
    const faceDepths = channel === 'onItem' && shape?.shapeKind === 'panel' && !billboard
        ? resolvePanelLabelFaceDepths({ shape, position, rotation, fontSize })
        : undefined;
    const opacity = clamp01(itemOpacity);
    const backgroundColor = declaration.backgroundColor;
    if (declaration.flag && (!Number.isFinite(declaration.flag.minTextPixels)
        || declaration.flag.minTextPixels <= 0
        || declaration.flag.postOffset.some((value) => !Number.isFinite(value)))) {
        throw new Error('Label flags require a finite post offset and positive minTextPixels.');
    }
    if (declaration.flag && declaration.flag.prongOffsets !== undefined
        && (declaration.flag.prongOffsets.length < 2
            || declaration.flag.prongOffsets.some(offset => offset.some(value => !Number.isFinite(value))))) {
        throw new Error('Branched label flags require at least two finite prong offsets.');
    }
    return {
        text,
        color: declaration.color ?? DEFAULT_COLOR,
        fontSize,
        padding,
        position,
        rotation: toEuler(rotation),
        billboard,
        doubleSidedReadable,
        ...(faceDepths ? { faceDepths } : {}),
        opacity,
        background: typeof backgroundColor === 'string' && backgroundColor.length > 0
            ? {
                color: backgroundColor,
                opacity: clamp01((declaration.backgroundOpacity ?? DEFAULT_BACKGROUND_OPACITY) * opacity),
            }
            : null,
        depthTest: declaration.depthTest ?? true,
        renderOrder: declaration.renderOrder ?? null,
        ...(declaration.flag ? { flag: declaration.flag } : {}),
    };
}
